import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d97pgs3558j8vijmgkg0';

export default function ContactSection() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypotValue = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setFormSubmitted(true);
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const searchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        searchParams.append(key, value as string);
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || 'Something went wrong. Please try again.';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="relative w-full py-24 md:py-32 px-4 md:px-6 bg-background-50">
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8 mb-20">
          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-4 tracking-wide uppercase">Location</h4>
            <div className="w-10 h-px bg-secondary-300 mb-4"></div>
            <p className="font-body text-sm text-foreground-600 leading-relaxed">
              Perkunkiemio g. 19<br />
              Vilnius, 12120<br />
              Lithuania
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-4 tracking-wide uppercase">Contact</h4>
            <div className="w-10 h-px bg-secondary-300 mb-4"></div>
            <p className="font-body text-sm text-foreground-600 leading-relaxed">
              +370 66806247<br />
              contact@technopankai.com
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-4 tracking-wide uppercase">Focus</h4>
            <div className="w-10 h-px bg-secondary-300 mb-4"></div>
            <p className="font-body text-sm text-foreground-600 leading-relaxed">
              iOS Platform<br />
              Modern Architecture<br />
              Performance Systems
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-4 tracking-wide uppercase">Standards</h4>
            <div className="w-10 h-px bg-secondary-300 mb-4"></div>
            <p className="font-body text-sm text-foreground-600 leading-relaxed">
              Clean Architecture<br />
              User-Centered Design<br />
              Continuous Innovation
            </p>
          </div>
        </div>

        <div className="border-t border-secondary-200/60 pt-16">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="lg:w-1/2">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-heading text-7xl md:text-8xl font-bold text-foreground-950 leading-none">VLN</span>
                <span className="font-heading text-7xl md:text-8xl font-bold text-secondary-300 leading-none">LT</span>
              </div>
              <div className="w-16 h-px bg-accent-500 mb-4"></div>
              <p className="font-body text-sm text-foreground-500">Based in Vilnius, Lithuania — delivering engineering excellence across Europe.</p>
            </div>

            <div className="lg:w-1/2">
              <div className="bg-background-100 rounded-2xl p-8">
                {formSubmitted ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                      <i className="ri-check-line text-accent-600 text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Message sent</h3>
                    <p className="font-body text-sm text-foreground-600">Thank you for reaching out. We will get back to you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="" id="contact-form">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">Send us a message</h3>

                    {formError && (
                      <div className="mb-5 p-4 rounded-lg bg-accent-100/60 text-accent-800 text-sm font-body">
                        {formError}
                      </div>
                    )}

                    <div className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="contact-name" className="font-label text-xs text-foreground-500 mb-1.5 block">Name</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-secondary-200/70 text-sm font-body text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="font-label text-xs text-foreground-500 mb-1.5 block">Email</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-secondary-200/70 text-sm font-body text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="font-label text-xs text-foreground-500 mb-1.5 block">Message</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={3}
                          maxLength={500}
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-secondary-200/70 text-sm font-body text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                          placeholder="Your message (max 500 characters)"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="antispam-field"
                      />

                      <button
                        type="submit"
                        className="w-full py-3 rounded-lg bg-foreground-950 text-background-50 font-label text-sm font-medium tracking-wide hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Send Message
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-secondary-200/60">
            <p className="font-body text-xs text-foreground-400">&copy; {new Date().getFullYear()} All rights reserved.</p>
            <div className="flex items-center gap-6 mt-4 sm:mt-0 text-foreground-300">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-circle-line text-xs"></i>
              </span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-square-line text-xs"></i>
              </span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-triangle-line text-xs"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}